import React, { useEffect, useState, useCallback } from 'react';
import StorageInfoModal from './StorageInfoModal';
import ProductImage from './ProductImage';
import LbsOzWeightInputs from './LbsOzWeightInputs';
import ProductInfoBaselineSection from './ProductInfoBaselineSection';
import { getNoteBySku, createNote, updateNote, deleteNote } from '../../services/productWarehouseNotesAPI';
import { getVariantWeight } from '../../services/SKUListAPI';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import { formatLbsOz, lbsOzToOunces, splitOunces } from '@/shared/utils/weightOz';
import type { ProductWarehouseNote } from '../../types/apiTypes';
import '../labels/PrintBoxLabel.css';
import './ProductInfoModal.css';

const WEIGHT_SAVE_FAILED = 'Failed to save product weight. Please try again.';
const WEIGHT_UPDATE_CONFIRM = 'Are you sure you want to update this item\'s weight?';

function parseNonNegativeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function formatNoteDate(isoString: string | undefined | null): string {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return isoString;
  }
}

interface ProductInfoItem {
  sku: string;
  name?: string;
  itemName?: string;
}

interface ProductInfoWeightUpdate {
  readonly onSave: (weightOz: number) => Promise<boolean>;
  readonly saving?: boolean;
}

interface ProductInfoModalProps {
  item: ProductInfoItem | null;
  onClose: () => void;
  weightUpdate?: ProductInfoWeightUpdate;
}

function ProductInfoModal({ item, onClose, weightUpdate }: ProductInfoModalProps): React.ReactElement | null {
  const [note, setNote] = useState<ProductWarehouseNote | null>(null);
  const [noteLoading, setNoteLoading] = useState(true);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lbs, setLbs] = useState('');
  const [oz, setOz] = useState('');
  const [weightOz, setWeightOz] = useState<number | null>(null);
  const [weightLoaded, setWeightLoaded] = useState(false);
  const [weightSaving, setWeightSaving] = useState(false);
  const [weightSaveError, setWeightSaveError] = useState<string | null>(null);

  const refreshNote = useCallback(async (source = 'unknown') => {
    if (!item?.sku) {
      setNote(null);
      setNoteLoading(false);
      setShowNoteEditor(false);
      return;
    }
    const showLoading = !String(source).startsWith('hybrid-');
    if (showLoading) {
      setNoteLoading(true);
    }
    try {
      const data = await getNoteBySku(item.sku);
      setNote((prev) => {
        const prevId = prev?.id ?? null;
        const nextId = data?.id ?? null;
        const prevText = prev?.note ?? '';
        const nextText = data?.note ?? '';
        const prevUpdatedAt = prev?.updated_at ?? null;
        const nextUpdatedAt = data?.updated_at ?? null;
        const prevCreatedAt = prev?.created_at ?? null;
        const nextCreatedAt = data?.created_at ?? null;
        if (
          prevId === nextId &&
          prevText === nextText &&
          prevUpdatedAt === nextUpdatedAt &&
          prevCreatedAt === nextCreatedAt
        ) {
          return prev;
        }
        return data;
      });
    } catch {
      setNote(null);
    } finally {
      if (showLoading) {
        setNoteLoading(false);
      }
    }
  }, [item?.sku]);

  useEffect(() => {
    setNote(null);
    setShowNoteEditor(false);
    setNoteInput('');
    setLbs('');
    setOz('');
    setWeightOz(null);
    setWeightLoaded(false);
    setWeightSaving(false);
    setWeightSaveError(null);
  }, [item?.sku]);

  useEffect(() => {
    refreshNote('initial');
  }, [refreshNote]);

  useEffect(() => {
    const sku = item?.sku;
    if (!sku) {
      setWeightOz(null);
      setWeightLoaded(false);
      return;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      const variant = await getVariantWeight(sku);
      if (cancelled) return;
      setWeightOz(variant?.weightOz ?? null);
      setWeightLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.sku]);

  useHybridShelfIndexSync({
    enabled: Boolean(item?.sku),
    onSync: (source) => refreshNote(`hybrid-${source}`),
    pollMs: 20000,
    tables: ['product_warehouse_notes'],
  });

  const handleAddNoteClick = () => {
    setShowNoteEditor(true);
    setNoteInput('');
  };

  const handleEditNoteClick = () => {
    setShowNoteEditor(true);
    setNoteInput(note?.note || '');
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!note?.id || noteSaving) return;
    setNoteSaving(true);
    setShowDeleteConfirm(false);
    try {
      await deleteNote(note.id);
      setNote(null);
    } catch (err: unknown) {
      console.error('Error deleting note:', err);
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleWriteNote = async () => {
    const trimmed = noteInput.trim();
    if (!trimmed || noteSaving) return;
    setNoteSaving(true);
    try {
      if (note) {
        const updated = await updateNote(note.id, trimmed);
        setNote(updated);
      } else {
        const created = await createNote(item!.sku, trimmed);
        setNote(created);
      }
      setShowNoteEditor(false);
      setNoteInput('');
    } catch (err: unknown) {
      console.error('Error saving note:', err);
    } finally {
      setNoteSaving(false);
    }
  };

  const hasNoteText = noteInput.trim().length > 0;
  const displayName = item?.name ?? item?.itemName ?? '';
  const storedSplit = splitOunces(weightOz ?? 0);
  const lbsNum = parseNonNegativeNumber(lbs);
  const ozNum = parseNonNegativeNumber(oz);
  const enteredOz = lbsNum != null && ozNum != null ? lbsOzToOunces(lbsNum, ozNum) : null;
  const canUpdateWeight = enteredOz != null
    && enteredOz > 0
    && enteredOz !== weightOz;
  const weightBusy = weightSaving || Boolean(weightUpdate?.saving);

  const handleUpdateWeight = async (): Promise<void> => {
    if (!weightUpdate || !canUpdateWeight || weightBusy || enteredOz == null) return;
    const confirmed = window.confirm(WEIGHT_UPDATE_CONFIRM);
    if (!confirmed) return;
    setWeightSaveError(null);
    setWeightSaving(true);
    const ok = await weightUpdate.onSave(enteredOz);
    if (!ok) {
      setWeightSaveError(WEIGHT_SAVE_FAILED);
      setWeightSaving(false);
    }
  };

  if (!item?.sku) return null;

  return (
    <div className="pbl-modal-overlay" onClick={onClose}>
      <div className="pbl-modal product-info-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose} aria-label="Close">×</button>

        <div className="product-info-content">
          <h2 className="product-info-sku">{renderSkuWithSoftBreaks(item.sku)}</h2>

          <ProductImage sku={item.sku} alt={displayName || item.sku} />

          {displayName && <div className="product-info-name">{displayName}</div>}

          {weightLoaded ? (
            <div className="product-info-weight">
              {weightOz != null
                ? `Weight ${formatLbsOz(weightOz)}`
                : 'No weight'}
            </div>
          ) : null}

          <div className="product-info-locations">
            <StorageInfoModal sku={item.sku} />
          </div>

          <div className="product-info-notes">
            <div className="product-info-notes-title">Notes</div>
            {noteLoading ? (
              <div className="product-info-notes-loading">Loading...</div>
            ) : (
              <>
                {note && !showNoteEditor && (
                  <>
                    {note.note && (
                      <div className="product-info-note-tag">
                        {note.updated_at
                          ? `Edited: ${formatNoteDate(note.updated_at)}`
                          : `Created: ${formatNoteDate(note.created_at)}`}
                      </div>
                    )}
                    {note.note && <div className="product-info-note-text">{note.note}</div>}
                  </>
                )}
                {showNoteEditor && (
                  <textarea
                    className="product-info-note-input"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder={note ? undefined : 'Enter note...'}
                    rows={3}
                    autoFocus
                  />
                )}
                {(!note || !note.note) && !showNoteEditor && (
                  <div className="product-info-note-btn-wrapper">
                    <button
                      type="button"
                      className="product-info-note-btn product-info-note-btn-add"
                      onClick={handleAddNoteClick}
                    >
                      Add Note
                    </button>
                  </div>
                )}
                {showNoteEditor && (
                  <button
                    type="button"
                    className={`product-info-note-btn product-info-note-btn-write ${hasNoteText ? 'active' : ''}`}
                    onClick={handleWriteNote}
                    disabled={!hasNoteText || noteSaving}
                  >
                    {noteSaving ? 'Saving...' : 'Write Note'}
                  </button>
                )}
                {note && note.note && !showNoteEditor && (
                  <div className="product-info-note-btn-wrapper product-info-note-btn-wrapper-row">
                    <button
                      type="button"
                      className="product-info-note-btn product-info-note-btn-add"
                      onClick={handleEditNoteClick}
                    >
                      Edit Note
                    </button>
                    <button
                      type="button"
                      className="product-info-note-btn product-info-note-btn-delete"
                      onClick={handleDeleteClick}
                      disabled={noteSaving}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <ProductInfoBaselineSection sku={item.sku} />

          {weightUpdate ? (
            <div className="product-info-weight-update">
              <LbsOzWeightInputs
                lbs={lbs}
                oz={oz}
                lbsPlaceholder={String(storedSplit.lbs)}
                ozPlaceholder={String(storedSplit.oz)}
                onLbsChange={setLbs}
                onOzChange={setOz}
                onLbsFocus={() => setLbs('')}
                onOzFocus={() => setOz('')}
                disabled={weightBusy}
              />
              {weightSaveError ? (
                <div className="product-info-weight-save-error" role="alert">
                  {weightSaveError}
                </div>
              ) : null}
              <button
                type="button"
                className="home-button"
                onClick={() => { void handleUpdateWeight(); }}
                disabled={!canUpdateWeight || weightBusy}
              >
                {weightBusy ? 'Saving...' : 'Update Product Weight'}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="pbl-modal-overlay product-info-delete-overlay" onClick={handleDeleteCancel}>
          <div className="pbl-modal product-info-delete-modal" onClick={(e) => e.stopPropagation()}>
            <p className="product-info-delete-message">Do you want to delete this note?</p>
            <div className="product-info-delete-buttons">
              <button
                type="button"
                className="product-info-note-btn product-info-note-btn-add"
                onClick={handleDeleteCancel}
              >
                Go Back
              </button>
              <button
                type="button"
                className="product-info-note-btn product-info-note-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={noteSaving}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductInfoModal;
